import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_PAGE_NUMBER } from '@components/listing-articles-blog/config';
import { ListingArticlesBlog } from '@components/listing-articles-blog/models';
import { ButtonComponent } from '@shared/components/button';
import { OptionCardComponent } from '@shared/components/option-card';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { BlogArticle } from '@shared/services/elastic-search/models';
import { LogsService } from '@shared/services/logs';
import { UrlParamsService } from '@shared/services/url-params';
import { take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-listing-articles-blog',
  templateUrl: './listing-articles-blog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OptionCardComponent, SvgComponent, RouterLink, NgTemplateOutlet, TitleComponent, ButtonComponent],
})
export class ListingArticlesBlogComponent extends AbstractDynamicComponent<ListingArticlesBlog> implements OnInit {
  protected readonly articles = signal<BlogArticle[]>([]);
  protected readonly featuredArticles = signal<BlogArticle[]>([]);
  protected readonly firstSixArticles = computed(() => this.articles().slice(0, 6));
  protected readonly remainingArticles = computed(() => this.articles().slice(6));
  protected isAllArticlesLoaded = false;
  private readonly urlParamsService = inject(UrlParamsService);
  private readonly logs = inject(LogsService);
  private readonly currentPageFromUrl = computed(() =>
    Number(this.urlParamsService.param('article_page')() ?? DEFAULT_PAGE_NUMBER)
  );
  private readonly elasticSearchService = inject(ElasticSearchService);
  private firstInit = true;
  private totalItems = 0;
  private remainingItems = 0;
  private nbArticlesLoaded = 0;
  private previousUrl: string = '';

  constructor() {
    super();
    effect(() => {
      const currentUrl = this.urlParamsService.currentUrl()();
      if (currentUrl !== this.previousUrl) {
        this.previousUrl = currentUrl;
        this.firstInit = true;
        this.totalItems = 0;
        this.remainingItems = 0;
        this.nbArticlesLoaded = 0;
        this.isAllArticlesLoaded = false;
      }
      const currentPage = this.currentPageFromUrl() ?? 1;
      const itemFrom = this.nbArticlesLoaded;
      const itemsToFetch = this.firstInit ? currentPage * DEFAULT_ITEMS_PER_PAGE : DEFAULT_ITEMS_PER_PAGE;
      if (!this.firstInit && this.totalItems !== 0 && this.remainingItems <= 0) return;
      this.elasticSearchService
        .getBlogArticles(this.data.blogType, itemFrom, itemsToFetch, this.data.level2Category)
        .pipe(
          take(1),
          tap((list) => {
            if (this.totalItems === 0) {
              this.totalItems = list.totalItems;
            }
            if (itemFrom === 0) {
              untracked(() => this.articles.set(list.items));
            } else {
              untracked(() => this.articles.update((articles) => [...articles, ...list.items]));
            }
            this.firstInit = false;
            this.nbArticlesLoaded = untracked(() => this.articles().length);
            this.remainingItems = this.totalItems - this.nbArticlesLoaded;
            if (this.remainingItems <= 0) {
              this.isAllArticlesLoaded = true;
              this.logs.info(
                'ListingArticlesBlogComponent',
                'All blog articles have been loaded',
                this.nbArticlesLoaded
              );
            }
          })
        )
        .subscribe({ error: (err) => this.logs.error('ListingArticlesBlogComponent', 'getBlogArticles failed', err) });
    });
  }

  ngOnInit() {
    this.elasticSearchService
      .getFeaturedBlogArticles(this.data.blogType)
      .pipe(
        take(1),
        tap((list) => {
          this.featuredArticles.set(list.items);
        })
      )
      .subscribe({
        error: (err) => this.logs.error('ListingArticlesBlogComponent', 'getFeaturedBlogArticles failed', err),
      });
  }

  loadMore() {
    this.urlParamsService.mergeParams({ article_page: this.currentPageFromUrl() + 1 });
  }
}
